import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Rocket, ShieldCheck, ShieldAlert, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "./ui/input-otp";
import { useAuth } from "../hooks/useAuth";
import { adminCheck, adminAuth, setAdminToken } from "../lib/api";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

export const AdminAccessModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { startLogin } = useAuth();
  const [state, setState] = useState({ loading: true });
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCode("");
    setState({ loading: true });
    adminCheck()
      .then((r) => {
        if (r.admin) {
          onClose();
          navigate("/admin");
          return;
        }
        setState({ loading: false, ...r });
      })
      .catch(() => setState({ loading: false, authenticated: false, eligible: false }));
  }, [open, navigate, onClose]);

  const verify = async () => {
    if (code.length !== 6) return;
    setVerifying(true);
    try {
      const res = await adminAuth(code);
      setAdminToken(res.admin_token);
      toast.success("Access granted");
      onClose();
      navigate("/admin");
    } catch (e) {
      const detail = e.response?.data?.detail || "Verification failed";
      toast.error(typeof detail === "string" ? detail : "Verification failed");
      setCode("");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[95] flex items-center justify-center p-4 bg-[#0F0F0F]/70 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          data-testid="admin-access-overlay"
        >
          <motion.div
            className="bg-white rounded-3xl w-full max-w-md shadow-2xl relative overflow-hidden"
            initial={{ scale: 0.95, opacity: 0, y: 12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 12 }}
            transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            data-testid="admin-access-modal"
          >
            <button
              onClick={onClose}
              className="absolute top-5 right-5 text-white/70 hover:text-white transition-colors z-10"
              data-testid="admin-modal-close"
            >
              <X size={22} />
            </button>

            <div className="bg-[#0F0F0F] px-8 pt-10 pb-10 text-center relative overflow-hidden">
              <div
                className="absolute inset-0 opacity-60"
                style={{ background: "radial-gradient(circle at 50% 20%, rgba(212,175,55,0.22) 0%, rgba(212,175,55,0) 55%)" }}
              />
              <div className="relative">
                <div className="mx-auto mb-4 w-14 h-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
                  <Rocket className="text-[#D4AF37]" size={26} />
                </div>
                <h2 className="font-display text-2xl font-bold text-white">Authorized Access</h2>
                <p className="text-sm text-[#A1A1AA] mt-2 max-w-xs mx-auto">
                  LaunchBS Command Center — restricted to authorized administrators.
                </p>
              </div>
            </div>

            <div className="px-8 py-8">
              {state.loading ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="animate-spin text-[#D4AF37]" size={26} />
                </div>
              ) : !state.authenticated ? (
                <div className="text-center">
                  <p className="text-sm text-[#687280] mb-6">
                    Sign in with your authorized Google account to continue.
                  </p>
                  <button
                    onClick={() => startLogin()}
                    className="w-full inline-flex items-center justify-center gap-3 bg-white border border-gray-200 rounded-full py-3.5 text-sm font-semibold text-[#0F0F0F] hover:bg-[#F7F7F7] transition-colors shadow-sm"
                    data-testid="admin-google-btn"
                  >
                    <GoogleIcon /> Continue with Google
                  </button>
                </div>
              ) : !state.eligible ? (
                <div className="text-center py-2" data-testid="admin-not-authorized">
                  <ShieldAlert className="mx-auto text-red-500 mb-3" size={30} />
                  <p className="font-display font-semibold text-[#0F0F0F]">Not authorized</p>
                  <p className="text-sm text-[#687280] mt-2">
                    <span className="font-medium">{state.email}</span> does not have admin access.
                  </p>
                </div>
              ) : (
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 text-xs font-medium text-emerald-600 bg-emerald-50 rounded-full px-3 py-1 mb-5">
                    <ShieldCheck size={13} /> {state.email}
                  </div>
                  <p className="text-sm text-[#687280] mb-5">Enter your 6-digit access code.</p>
                  <div className="flex justify-center mb-6">
                    <InputOTP
                      maxLength={6}
                      value={code}
                      onChange={setCode}
                      onComplete={verify}
                      data-testid="admin-code-input"
                    >
                      <InputOTPGroup className="gap-2">
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                          <InputOTPSlot
                            key={i}
                            index={i}
                            className="h-12 w-11 text-lg rounded-lg border-gray-200"
                          />
                        ))}
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                  <button
                    onClick={verify}
                    disabled={code.length !== 6 || verifying}
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#0F0F0F] text-white rounded-full py-3.5 text-sm font-semibold hover:bg-[#D4AF37] hover:text-[#0F0F0F] transition-colors disabled:opacity-40"
                    data-testid="admin-verify-btn"
                  >
                    {verifying ? <Loader2 className="animate-spin" size={16} /> : "Verify Access"}
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
